import { useEffect, useMemo, useState } from "react"
import { AccountBalance, AssignmentOutlined, SwapVert, Tune } from "@mui/icons-material"
import { Collapse, IconButton, Tooltip } from "@mui/material"
import intl from "src/internationalization/intl"
import { CircularProgress, ClientsOverviewCard, Grid, IconWrapper, InnerSection, OverviewCard, Section, TextInput } from "src/lib"
import { FinanceMode } from "src/lib/icons"
import { formatCurrencyBRL } from "src/lib/transformers/ClientsOverviewInfo"
import { useCommitmentsStore, useOpportunitiesStore, useClientsOverviewStore } from "src/store"
import { useDrawerStore } from "src/store/drawerStore"
import { useMessageStore } from "src/store/messageStore"
import { fetchClientsOverview } from "src/services/api/clientsOverview"

const OverviewSection = () => {
    const commitmentsData = useCommitmentsStore((s: any) => s.commitmentsData)
    const opportunitiesData = useOpportunitiesStore((s: any) => s.opportunitiesData)
    const clientsOverviewData = useClientsOverviewStore((s: any) => s.clientsOverviewData)
    const setClientsOverviewData = useClientsOverviewStore((s: any) => s.setClientsOverviewData)
    const actualContext = useMessageStore((s: any) => s.actualContext)
    const setJsonContext = useMessageStore((s: any) => s.setJsonContext)
    const setActualContext = useMessageStore((s: any) => s.setActualContext)
    const openDrawer = useDrawerStore((s: any) => s.openDrawer)

    const [minNominal, setMinNominal] = useState<string>("")
    const [minRelative, setMinRelative] = useState<string>("")
    const [showCashFilters, setShowCashFilters] = useState(false)

    const cashFiltersActive = minNominal !== "" || minRelative !== ""

    useEffect(() => {
        const loadClientsOverview = async () => {
            try {
                const response = await fetchClientsOverview(3)
                setClientsOverviewData(response)
            } catch (error) {
                console.error("Erro ao buscar clients overview no OverviewSection", error)
            }
        }

        if (!clientsOverviewData) {
            loadClientsOverview()
        }
    }, [clientsOverviewData, setClientsOverviewData])

    const { uniqueClientsCount, totalNetAmount } = useMemo(() => {
        if (!commitmentsData?.commitments) {
            return { uniqueClientsCount: 0, totalNetAmount: 0 }
        }

        const uniqueClients = new Set<string>()
        let netAmountSum = 0

        commitmentsData.commitments.forEach((commitment: any) => {
            commitment.clients?.forEach((clientWrapper: any) => {
                const clientInfo = clientWrapper?.client
                if (clientInfo?.name && clientInfo?.account !== undefined) {
                    const uniqueKey = `${clientInfo.name}-${clientInfo.account}`
                    uniqueClients.add(uniqueKey)
                }

                if (typeof clientWrapper?.net_amount === "number") {
                    netAmountSum += clientWrapper.net_amount
                }
            })
        })

        return {
            uniqueClientsCount: uniqueClients.size,
            totalNetAmount: netAmountSum,
        }
    }, [commitmentsData])

    const { uniqueImpactedClientsCount, totalOpportunitiesVolume } = useMemo(() => {
        if (!opportunitiesData?.objectives) {
            return { uniqueImpactedClientsCount: 0, totalOpportunitiesVolume: 0 }
        }

        const uniqueClients = new Set<string>()
        let volumeSum = 0

        opportunitiesData.objectives.forEach((objective: any) => {
            objective.impacted_clients?.forEach((impactedClient: any) => {
                const clientInfo = impactedClient?.p ?? impactedClient
                if (clientInfo?.name && clientInfo?.account !== undefined) {
                    const uniqueKey = `${clientInfo.name}-${clientInfo.account}`
                    uniqueClients.add(uniqueKey)
                }

                if (typeof impactedClient?.volumeOportunidade === "number") {
                    volumeSum += impactedClient.volumeOportunidade
                }
            })
        })

        return {
            uniqueImpactedClientsCount: uniqueClients.size,
            totalOpportunitiesVolume: volumeSum,
        }
    }, [opportunitiesData])

    const { uniqueCashClientsCount, totalCashNetWorth } = useMemo(() => {
        const customers = commitmentsData?.customers_with_cash_to_invest
        if (!customers) {
            return { uniqueCashClientsCount: 0, totalCashNetWorth: 0 }
        }

        const nominalThreshold = minNominal !== "" ? parseFloat(minNominal) : null
        const relativeThreshold = minRelative !== "" ? parseFloat(minRelative) / 100 : null

        const uniqueClients = new Set<string>()
        let netWorthSum = 0

        customers.forEach((item: any) => {
            const cashAmount: number = item?.amount
            const netWorth: number = item?.net_worth

            if (nominalThreshold !== null && typeof cashAmount === "number" && cashAmount < nominalThreshold) return
            if (relativeThreshold !== null && typeof cashAmount === "number" && typeof netWorth === "number" && netWorth > 0 && cashAmount / netWorth < relativeThreshold) return

            const clientInfo = item?.cliente
            if (clientInfo?.name && clientInfo?.account !== undefined) {
                const uniqueKey = `${clientInfo.name}-${clientInfo.account}`
                uniqueClients.add(uniqueKey)
            }

            if (typeof netWorth === "number") {
                netWorthSum += netWorth
            }
        })

        return {
            uniqueCashClientsCount: uniqueClients.size,
            totalCashNetWorth: netWorthSum,
        }
    }, [commitmentsData, minNominal, minRelative])

    const { overviewClientsTotal, overviewTotalAmount } = useMemo(() => {
        const numberOfClients = clientsOverviewData?.number_of_clients ?? 0
        const totalAmount = clientsOverviewData
            ? formatCurrencyBRL(clientsOverviewData.total_amount)
            : formatCurrencyBRL(0)

        return {
            overviewClientsTotal: numberOfClients,
            overviewTotalAmount: totalAmount,
        }
    }, [clientsOverviewData])

    const handleClickAIButton = () => {
        if (actualContext !== 'overview') {
            try {
                setActualContext('overview')
                
                const overviewData = {
                    commitments: commitmentsData,
                    opportunities: opportunitiesData,
                    clientsOverview: clientsOverviewData,
                }
                setJsonContext(overviewData)
            } catch (error) {
                console.error("Error setting overview context:", error)
            }
        }
        openDrawer()
    }

    const isLoadingOverview =
        !commitmentsData ||
        !opportunitiesData ||
        !clientsOverviewData

    return (
        <Section
            title={intl("overview")}
            icon={<FinanceMode />}
            onClickAIButton={handleClickAIButton}
        >
            <InnerSection title={intl("yourWeek")}>
                {isLoadingOverview ? (
                    <Grid container justifyContent="center" sx={{ mt: 4, mb: 2 }}>
                        <CircularProgress color="primary" />
                    </Grid>
                ) : (
                    <Grid container gap={2} mt={2} alignItems="flex-start">
                        <OverviewCard
                            title={intl("liquidityEvents")}
                            clientsNumber={uniqueClientsCount}
                            totalAmount={formatCurrencyBRL(totalNetAmount)}
                            icon={
                                <IconWrapper color="tertiary">
                                    <SwapVert />
                                </IconWrapper>
                            }
                        />
                        <OverviewCard
                            title={intl("opportunities")}
                            clientsNumber={uniqueImpactedClientsCount}
                            totalAmount={formatCurrencyBRL(totalOpportunitiesVolume)}
                            icon={
                                <IconWrapper color="quaternary">
                                    <AssignmentOutlined />
                                </IconWrapper>
                            }
                        />
                        <OverviewCard
                            title={intl("cashOnHand")}
                            clientsNumber={uniqueCashClientsCount}
                            totalAmount={formatCurrencyBRL(totalCashNetWorth)}
                            minWidth={400}
                            icon={
                                <IconWrapper color="quaternary">
                                    <AccountBalance />
                                </IconWrapper>
                            }
                            headerAction={
                                <Tooltip title="Filtrar por caixa mínimo">
                                    <IconButton
                                        size="small"
                                        onClick={() => setShowCashFilters(v => !v)}
                                        sx={{ color: cashFiltersActive ? "primary.main" : "text.secondary" }}
                                    >
                                        <Tune fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            }
                            footer={
                                <Collapse in={showCashFilters} unmountOnExit>
                                    <Grid container gap={1} sx={{ pt: 1 }} wrap="nowrap">
                                        <Tooltip title="Exclui clientes com valor em caixa abaixo deste montante (R$)" placement="bottom">
                                            <span>
                                                <TextInput
                                                    label={intl("cashMinNominal")}
                                                    type="number"
                                                    value={minNominal}
                                                    onChange={(e: any) => setMinNominal(e.target.value)}
                                                    size="small"
                                                    sx={{ width: 180 }}
                                                />
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Exclui clientes cujo caixa representa menos que este percentual do patrimônio total (%)" placement="bottom">
                                            <span>
                                                <TextInput
                                                    label={intl("cashMinRelative")}
                                                    type="number"
                                                    value={minRelative}
                                                    onChange={(e: any) => setMinRelative(e.target.value)}
                                                    size="small"
                                                    sx={{ width: 180 }}
                                                />
                                            </span>
                                        </Tooltip>
                                    </Grid>
                                </Collapse>
                            }
                        />
                    </Grid>
                )}
            </InnerSection>
            <InnerSection title={intl("yourClients")} mt={2}>
                {isLoadingOverview ? (
                    <Grid container justifyContent="center" sx={{ mt: 2, mb: 2 }}>
                        <CircularProgress color="primary" />
                    </Grid>
                ) : (
                    <Grid container gap={2} mt={2}>
                        <ClientsOverviewCard
                            numberOfClients={overviewClientsTotal}
                            totalAmount={overviewTotalAmount}
                        />
                    </Grid>
                )}
            </InnerSection>
        </Section>
    )
}

export { OverviewSection }

